import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { ExternalStorageModels } from '@models/external-storage.models';
import { ExternalStorageActions } from '@states/external-storage/external-storage.action-types';
import ExternalStorageDocument = ExternalStorageModels.ExternalStorageDocument;
import ExternalStorageType = ExternalStorageModels.ExternalStorageType;

export interface ExternalStorageState extends EntityState<ExternalStorageModels.ExternalStorageDocument> {
  isInitialLoaded: boolean;
  isLoading: boolean;
  filters: {
    query: string
  };
  isSaving: boolean;
  selectedExternalStorageDocument: ExternalStorageModels.ExternalStorageDocument;
  isFormValid: boolean;
  canDeactivate: boolean;
  testPassed: boolean;
  testLoading: boolean;
  testFinished: boolean;
  externalStorageConnected: boolean;
  isDeleting: boolean
}

export const adapter: EntityAdapter<ExternalStorageModels.ExternalStorageDocument> = createEntityAdapter<ExternalStorageModels.ExternalStorageDocument>({
  selectId: (document: ExternalStorageModels.ExternalStorageDocument) => document._id,
});


export const initialState: ExternalStorageState = adapter.getInitialState({
  isInitialLoaded: false,
  isLoading: false,
  filters: {
    query: null,
  },
  isSaving: false,
  testPassed: false,
  testLoading: false,
  testFinished: false,
  selectedExternalStorageDocument: null,
  isFormValid: false,
  canDeactivate: false,
  externalStorageConnected: false,
  isDeleting: false
});

export const externalStorageStateReducer = createReducer(
  initialState,
  on(ExternalStorageActions.resetToInitialState, state => {
    return {
      ...initialState,
    };
  }),
  on(ExternalStorageActions.getExternalStorage, state => {
    return {
      ...state,
      isLoading: true
    };
  }),
  on(ExternalStorageActions.getExternalStorageFail, state => {
    return {
      ...state,
      isLoading: false,
      isInitialLoaded: false
    };
  }),
  on(ExternalStorageActions.getExternalStorageSuccess, (state, { documents }) => {
    return adapter.setMany(documents, {
      ...state,
      isLoading: false,
      isInitialLoaded: true,
    });
  }),
  on(ExternalStorageActions.setSelectedExternalStorage, (state, { document }) => {
    return {
      ...state,
      selectedExternalStorageDocument: document,
    };
  }),
  on(ExternalStorageActions.setQueryStringFilter, (state, { prop, value }) => {
    return {
      ...state,
      isLoading: true,
      filters: {
        ...state.filters,
        [prop]: value,
      },
    };
  }),
  on(ExternalStorageActions.setCanDeactivate, (state, { canDeactivate }) => {
      return {
        ...state,
        canDeactivate,
      };
    }),
  on(ExternalStorageActions.resetEntities, (state) => {
    return {
      ...state,
      entities: initialState.entities,
    };
  }),
  on(ExternalStorageActions.testExternalStorageSuccess, (state) => {
    return {
      ...state,
      externalStorageConnected: true,
      testPassed: true,
      testLoading: false,
      testFinished: true
    };
  }),
  on(ExternalStorageActions.testExternalStorageError, (state) => {
    return {
      ...state,
      testPassed: false,
      testLoading: false,
      testFinished: true
    };
  }),
  on(ExternalStorageActions.setIsSaving, (state, { isSaving }) => {
    return {
      ...state,
      isSaving: isSaving,
    };
  }),
  on(ExternalStorageActions.testExternalStorage, ( state ) => {
    return {
      ...state,
      testPassed: false,
      testLoading: true,
      testFinished: false,
    }
  }),
  on(ExternalStorageActions.saveExternalStorageSetOne, (state, { document }) => {
    return adapter.addOne(document, {
      ...state,
    });
  }),
  on(ExternalStorageActions.deleteExternalStorageSuccess, (state, { storageId }) => {
    return adapter.removeOne(storageId, {
      ...state,
      isDeleting: false
    });
  }),
  on(ExternalStorageActions.getOneExternalStorageSuccess, (state, { document }) => {
    return {
      ...state,
      selectedExternalStorageDocument: document,
      isLoading: false
    };
  }),
  on(ExternalStorageActions.getOneExternalStorage, (state) => ({
    ...state,
    isLoading: true
  })),
  on(ExternalStorageActions.deleteExternalStorageFail, (state) => ({
    ...state,
    isDeleting: false
  })),
  on(ExternalStorageActions.startDeleteExternalStorage, (state) => ({
    ...state,
    isDeleting: true
  })),
  on(ExternalStorageActions.getOneExternalStorageFail, (state) => ({
    ...state,
    isLoading: false
  })),
  on(ExternalStorageActions.changeFormValidation, (state, { isFormValid }) => {
    return {
      ...state,
      isFormValid,
    };
  }),
);
